// pages/Category.jsx
import React, { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { apiFetch } from "../api";
import { useAuth  } from "../context/AuthContext";
import "../styles/pages/_category.css";
import { timeAgo, formatDate, formatDateTime, TimeAgo } from "../helpers/time";

export default function Category() {
    const { categoryId }              = useParams();
    const { user }                    = useAuth();
    const [category, setCategory]     = useState(null);
    const [threads, setThreads]       = useState([]);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(null);
            try {
                const data = await apiFetch(`/api/forum/categories/${categoryId}`);
                if (!cancelled) {
                    setCategory(data.category);
                    setThreads(data.threads || []);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => { cancelled = true; };
    }, [categoryId]);

    // RENDER STATES

    if (loading) {
        return (
            <div className="wrapper category">
                <div className="category__loading">Loading category…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="wrapper category">
                <Link to="/forum" className="category__back">← Back to Forum</Link>
                <div className="category__error">
                    <h2>Category not found</h2>
                    <p className="category__error-detail">{error}</p>
                </div>
            </div>
        );
    }

    // MAIN RENDER

    return (
        <div className="wrapper category">
            <Link to="/forum" className="category__back">← Back to Forum</Link>

            <header className="category__header">
                <div className="category__header-top">
                    <div>
                        <h1 className="category__title">{category.name.toUpperCase()}</h1>
                        {category.description && (
                            <p className="category__desc">{category.description}</p>
                        )}
                        <div className="category__stats">
                            <span>{category.thread_count} thread{category.thread_count !== 1 ? "s" : ""}</span>
                            <span className="category__stats-divider">·</span>
                            <span>{category.post_count} post{category.post_count !== 1 ? "s" : ""}</span>
                        </div>
                    </div>

                    {user && (
                        <Link
                            to={`/forum/${categoryId}/new`}
                            className="btn btn--danger"
                        >
                            + New Thread
                        </Link>
                    )}
                </div>
            </header>

            <div className="category__content">
                {threads.length === 0 ? (
                    <p className="category__empty">
                        No threads yet. Be the first to start a discussion!
                    </p>
                ) : (
                    <div className="category__thread-list">
                        {/* Table header */}
                        <div className="category__thread-row category__thread-row--header">
                            <div className="category__thread-col category__thread-col--title">
                                Thread
                            </div>
                            <div className="category__thread-col category__thread-col--stat">
                                Posts
                            </div>
                            <div className="category__thread-col category__thread-col--activity">
                                Last Reply
                            </div>
                        </div>

                        {/* Thread rows */}
                        {threads.map((thread) => (
                            <Link
                                key={thread.id}
                                to={`/thread/${thread.id}`}
                                className={`category__thread-row${thread.pinned ? " category__thread-row--pinned" : ""}`}
                            >
                                <div className="category__thread-col category__thread-col--title">
                                    <div className="category__thread-badges">
                                        {thread.pinned && (
                                            <span className="category__badge category__badge--pinned">
                                                Pinned
                                            </span>
                                        )}
                                        {thread.locked && (
                                            <span className="category__badge category__badge--locked">
                                                Locked
                                            </span>
                                        )}
                                    </div>
                                    <span className="category__thread-name">{thread.title}</span>
                                    <span className="category__thread-meta">
                                        by {thread.author_name} · {formatDate(thread.created_at)}
                                    </span>
                                </div>
                                <div className="category__thread-col category__thread-col--stat">
                                    <span className="category__stat-number">{thread.post_count}</span>
                                    <span className="category__stat-label">
                                        post{thread.post_count !== 1 ? "s" : ""}
                                    </span>
                                </div>
                                <div className="category__thread-col category__thread-col--activity">
                                    {thread.last_reply_by ? (
                                        <>
                                            <span className="category__activity-user">
                                                {thread.last_reply_by}
                                            </span>
                                            <span className="category__activity-time">
                                                {timeAgo(thread.last_reply_at)}
                                            </span>
                                        </>
                                    ) : (
                                        <span className="category__activity-time">No replies</span>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}